import type { ServicePageData } from './types';

const herasFencingBanners: ServicePageData = {
  slug: 'heras-fencing-banners',
  eyebrow: 'Signage',
  title: 'Turn temporary fencing into',
  titleAccent: 'branded site hoarding.',
  intro:
    'Heras fence panels go up around every building site, car park and outdoor event, so they are prime space for a bit of branding. We print banners cut to fit a standard panel and fix straight on with cable ties.',
  heroImage: '/home/pavement-signs/pavement-sign-wildjac.jpg',

  useCases: [
    { title: 'Building sites', text: 'Turn plain perimeter fencing into a branded hoarding while the works are on.' },
    { title: 'Events and festivals', text: 'Screen off a compound or back-of-house area without it looking bare.' },
    { title: 'Car parks', text: 'Wrap temporary fencing around a closed section or overflow area.' },
    { title: 'Construction marketing', text: 'Show off the finished development with renders and contact details.' },
    { title: 'Sports and outdoor events', text: 'Sponsor boards along a temporary perimeter.' },
  ],

  optionsHeading: 'Choose your fencing banner',
  optionsIntro: 'Sized to a standard Heras panel and fixed straight on with cable ties.',
  options: [
    {
      id: 'standard-pvc',
      title: 'Standard PVC',
      lead: 'A solid, fully printed banner for maximum visual impact.',
      body: 'A tough PVC banner that covers the panel edge to edge, giving your artwork the biggest possible presence. Best where the fence line does not need to let much wind through.',
      image: '/home/pavement-signs/pavement-sign-atelier.jpg',
      specs: [
        { label: 'Size', value: 'Sized to a standard 3.5m x 2m Heras panel, or made to your measurements.' },
        { label: 'Material', value: '510gsm PVC banner.' },
        { label: 'Finish', value: 'Full-colour, single-sided.' },
        { label: 'Fixings', value: 'Eyelets around the edge, cable ties supplied.' },
      ],
    },
    {
      id: 'mesh',
      title: 'Wind-permeable mesh',
      lead: 'Lets wind pass through, so the banner and the fence stay put.',
      body: 'A perforated mesh material that reduces wind load through the panel, which matters on longer runs of fencing or exposed sites where a solid banner can act like a sail.',
      image: '/home/pavement-signs/pavement-sign-tattoo.jpg',
      specs: [
        { label: 'Size', value: 'Sized to a standard 3.5m x 2m Heras panel, or made to your measurements.' },
        { label: 'Material', value: 'Wind-permeable PVC mesh.' },
        { label: 'Finish', value: 'Full-colour print, visible from the front.' },
        { label: 'Fixings', value: 'Eyelets around the edge, cable ties supplied.' },
      ],
    },
    {
      id: 'multi-panel-runs',
      title: 'Multi-panel runs',
      lead: 'One design carried across a whole run of fencing.',
      body: 'For a longer perimeter, artwork is planned across multiple banners so the design lines up panel to panel, whether that is a single message or a repeated pattern.',
      image: '/home/pavement-signs/pavement-sign-hkh.jpg',
      specs: [
        { label: 'Coverage', value: 'Any number of panels, laid out as one continuous design.' },
        { label: 'Material', value: 'Standard PVC or wind-permeable mesh, mixed if needed.' },
        { label: 'Fixings', value: 'Cable ties supplied for every panel.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Artwork, renders and photography printed directly onto the banner material on our in-house wide-format printer, with no minimum order.',
    },
    {
      title: 'Cut vinyl lettering',
      text: 'For a simple message such as a company name or phone number, lettering can be cut from coloured vinyl and applied to a plain banner instead of a full print.',
    },
  ],

  gallery: [
    '/home/pavement-signs/pavement-sign-atelier.jpg',
    '/home/pavement-signs/pavement-sign-wildjac.jpg',
    '/home/pavement-signs/pavement-sign-hkh.jpg',
    '/home/pavement-signs/pavement-sign-tattoo.jpg',
    '/home/pavement-signs/pavement-sign-petalbar.jpg',
    '/home/pavement-signs/pavement-sign-salon4.jpg',
  ],

  info: [
    {
      title: 'Supply only',
      text: 'Every banner is delivered with eyelets fitted and cable ties included, ready to fix straight to the fence.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Installed',
      text: 'Our insured install team can fit banners across a whole site or perimeter for you, including multi-panel runs.',
    },
    {
      title: 'Turnaround',
      text: '5 to 10 working days from approval. Need fencing dressed before a site opens? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'Will the banner fit our fence panels?',
      a: 'Standard panels are 3.5m x 2m, which is what we size to by default. If yours are a different size, send us the measurements and we will match them.',
    },
    {
      q: 'Should I choose mesh or standard PVC?',
      a: 'Mesh is the safer choice for longer runs or exposed sites, since it lets wind through rather than pulling on the fence. Standard PVC gives a bolder, fully solid graphic where wind is less of a concern.',
    },
    {
      q: 'How is it fixed to the fence?',
      a: 'Eyelets around the edge of the banner with cable ties, which we supply. No tools needed for a self-install.',
    },
  ],

  cta: {
    title: 'Get your free Heras fencing banner quote.',
    text: 'Tell us the panel sizes and how many you need, and we will put together a free mockup.',
    image: '/home/pavement-signs/pavement-sign-atelier.jpg',
  },
};

export default herasFencingBanners;
